import * as SpeechSDK from "microsoft-cognitiveservices-speech-sdk";
import {
  SpeakerAudioDestination,
  SpeechSynthesizer,
} from "microsoft-cognitiveservices-speech-sdk";
import {
  TextToSpeechService,
  TextToSpeechServiceConfig,
} from "@service/text-to-speech/text-to-speech-service";
import { ResultReason } from "microsoft-cognitiveservices-speech-sdk/distrib/lib/src/sdk/Exports";

export class AzureTextToSpeechService implements TextToSpeechService {
  private config: TextToSpeechServiceConfig | null = null;
  private synthesizer: SpeechSynthesizer | null = null;
  private player: SpeakerAudioDestination | null = null;

  init(config: TextToSpeechServiceConfig) {
    if (this.config) {
      this.destroy();
    }
    this.config = config;
  }

  async convertTextToSpeechAndPlay(
    text: string,
    voiceName: string,
    language: string,
  ): Promise<void> {
    if (!this.config) {
      console.error("Init should be called first");
    }
    if (!this.synthesizer) {
      this.synthesizer = this.createSpeechSynthesizer(
        this.config!,
        voiceName,
        language,
      );
    }

    return new Promise((resolve, reject) => {
      try {
        if (this.player) {
          this.player.onAudioEnd = () => resolve();
        }
        this.synthesizer?.speakTextAsync(
          text,
          (res) => {
            if (res.reason === ResultReason.Canceled) {
              reject();
            }
            if (
              [
                ResultReason.Canceled,
                ResultReason.SynthesizingAudioCompleted,
              ].includes(res.reason)
            ) {
              this.synthesizer?.close();
              this.synthesizer = null;
            }
          },
          (err) => reject(err),
        );
      } catch (e) {
        reject(e);
      }
    });
  }

  async cancelSpeech(): Promise<void> {
    this.player?.pause();
  }

  async destroy(): Promise<void> {
    try {
      this.player?.close();
      this.synthesizer?.close();
      this.player = null;
      this.synthesizer = null;
    } catch (e) {
      console.error(e);
    }
  }

  private createSpeechSynthesizer(
    config: TextToSpeechServiceConfig,
    voiceName: string,
    language: string,
  ): SpeechSynthesizer {
    const { accessToken, region } = config;

    const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
      accessToken,
      region!,
    );
    speechConfig.speechSynthesisVoiceName = voiceName;
    if (language) {
      speechConfig.speechSynthesisLanguage = language;
    }

    this.player = new SpeakerAudioDestination();
    const audioConfig = SpeechSDK.AudioConfig.fromSpeakerOutput(this.player);

    return new SpeechSDK.SpeechSynthesizer(speechConfig, audioConfig);
  }
}
